import { useEffect, useMemo, useState } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { InferType, object, ObjectSchema, string } from 'yup';

import { SetInvestmentModel, useGetInvestmentsByIdQuery, usePutInvestmentsByIdMutation } from '@client/api';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CardTitle,
    CurrencyAdornment,
    CurrencyAutocomplete,
    CurrencyCode,
    Datepicker,
    DD,
    DT,
    FormCancelButton,
    FormField,
    FormSubmitButton,
    getCurrencySymbol,
    GridCol,
    GridRow,
    Input,
    isDefinedNumber,
    isDefinedString,
    moneyValueSchema,
    openDateIntervalSchema,
    toCurrencyStringOrUndefined,
    toIntervalEndDateString,
    toIntervalStartDateString,
    toUTCDate,
    transformDateToIsoDateString
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

import { CancelRequestModal } from '../../../../../components/';
import { useTenantSettingsDefaults } from '../../../../../hooks';

type FormModel = SetInvestmentModel;

const schema: ObjectSchema<FormModel> = object({
  name: string().required(),
  purpose: string().nullable(),
  amount: moneyValueSchema.nullable().makeOptional(),
  duration: object({
    start: string().required().transform(transformDateToIsoDateString),
    end: string().required().transform(transformDateToIsoDateString)
  }),
  returnDuration: openDateIntervalSchema,
});

type FormValues = InferType<typeof schema>;
type FormPath = FieldPath<FormModel>
type FormDefaultValues = DeepPartial<FormValues>;

export interface InvestmentGeneralInformationCardProps {
  className?: string;
}

export function InvestmentGeneralInformationCard({ className }: InvestmentGeneralInformationCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const { id } = useParams<{ id: string }>();
  const { data: investment, isFetching } = useGetInvestmentsByIdQuery({ id: id ?? '' }, { skip: id == null });

  const [mutation] = usePutInvestmentsByIdMutation();

  const [editing, setEditing] = useState<boolean>(false);

  const handleOpenEdit = () => {
    setEditing(true);
  };

  const onSubmit = async (data: FormValues) => {
    if (id == null || investment == null) return;

    try {
      await mutation({
        id: investment.id,
        body: {
          ...investment,
          ...data
        },
      }).unwrap();
    } catch (e) {
      return;
    }

    setEditing(false);
  };

  const { defaultCurrencyCode } = useTenantSettingsDefaults();

  const defaultValues: FormDefaultValues = useMemo(() => ({
    name: investment?.name ?? '',
    purpose: investment?.purpose ?? '',
    amount: {
      amount: investment?.amount?.amount ?? '' as any,
      currency: {
        code: investment?.amount?.currency?.code ?? defaultCurrencyCode ?? ''
      }
    },
    duration: {
      start: investment?.duration?.start ? toUTCDate(investment?.duration?.start) as any : '',
      end: investment?.duration?.end ? toUTCDate(investment?.duration?.end) as any : '',
    },
    returnDuration: {
      start: investment?.returnDuration?.start ? toUTCDate(investment?.returnDuration?.start) as any : '',
      end: investment?.returnDuration?.end ? toUTCDate(investment?.returnDuration?.end) as any : '',
    }
  }), [investment?.amount?.amount, investment?.amount?.currency?.code, investment?.duration?.end, investment?.duration?.start, investment?.name, investment?.purpose, investment?.returnDuration?.end, investment?.returnDuration?.start, defaultCurrencyCode])

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const { handleSubmit, formState: { isDirty }, reset, control } = methods;

  const unitCurrencyCode = useWatch({ control, name: 'amount.currency.code', defaultValue: '' });

  const unitCurrencySymbol =
    isDefinedString(unitCurrencyCode) && isDefinedString(getCurrencySymbol(unitCurrencyCode))
      ? getCurrencySymbol(unitCurrencyCode)
      : undefined;

  const [cancelingRequest, setCancelingRequest] = useState<boolean>(false);

  const checkCancelRequest = () => {
    if (isDirty) {
      setCancelingRequest(true);
    } else {
      setEditing(false);
    }
  };

  const handleConfirmCancelRequest = () => {
    setEditing(false);
    setCancelingRequest(false);
  };

  const handleCancelCancelRequest = () => {
    setCancelingRequest(false);
  };

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Card className={className}>
        <FormProvider {...methods}>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            {/* Headline */}
            <CardHeader>
              <CardTitle className="flex-1" variant="dense">
                {tGlobal('investment.generalInformation')}
              </CardTitle>
              <CardActions>
                {!editing ? (
                  <Button
                    color="primary-dark"
                    disabledVariant="dark"
                    size="sm"
                    onClick={handleOpenEdit}
                    disabled={isFetching || id == null || investment == null}
                  >
                    {tCommon('edit')}
                  </Button>
                ) : (
                  <>
                    <FormSubmitButton type="submit" size="sm" disabled={isFetching}>
                      {tCommon('save')}
                    </FormSubmitButton>
                    <FormCancelButton size="sm" onClick={checkCancelRequest}>
                      {tCommon('cancel')}
                    </FormCancelButton>
                  </>
                )}
              </CardActions>
            </CardHeader>

            {/* Content */}
            <CardContent>
              <div className="space-y-2">
                <div className="py-2">
                  <GridRow cols={3} spacing={2} className="items-center">
                    <GridCol span={1}>
                      <DT>{tGlobal('investment.name')}</DT>
                    </GridCol>
                    <GridCol span={2} loading={isFetching}>
                      {!editing ? (
                        <DD isEmpty={!isDefinedString(investment?.name)}>
                          {investment?.name}
                        </DD>
                      ) : (
                        <FormField<FormValues, FormPath>
                          name="name"
                          defaultValue={''}
                          render={({ field }) => <Input size="small" {...field} />}
                        />
                      )}
                    </GridCol>

                    <GridCol span={1}>
                      <DT>{tGlobal('investment.purpose')}</DT>
                    </GridCol>
                    <GridCol span={2} loading={isFetching}>
                      {!editing ? (
                        <DD isEmpty={!isDefinedString(investment?.purpose)}>
                          {investment?.purpose}
                        </DD>
                      ) : (
                        <FormField<FormValues, FormPath>
                          name="purpose"
                          defaultValue={''}
                          render={({ field }) => <Input size="small" {...field} />}
                        />
                      )}
                    </GridCol>

                    <GridCol span={1}>
                      <DT>{tGlobal('investment.unitAmount')}</DT>
                    </GridCol>
                    <GridCol span={2} loading={isFetching}>
                      {!editing ? (
                        <DD className="font-medium" isEmpty={!isDefinedNumber(investment?.amount?.amount)}>
                          {toCurrencyStringOrUndefined(investment?.amount?.amount, undefined, {
                            currency: investment?.amount?.currency?.code as CurrencyCode,
                          })}
                        </DD>
                      ) : (
                        <GridRow cols={3} spacingX={1}>
                          <GridCol span={2}>
                            <FormField<FormValues, FormPath>
                              name="amount.amount"
                              defaultValue={''}
                              required
                              render={({ field }) => (
                                <CurrencyAdornment position="start" symbol={unitCurrencySymbol}>
                                  <Input type="number" size="small" className="pl-6" {...field} />
                                </CurrencyAdornment>
                              )}
                            />
                          </GridCol>
                          <GridCol span={1}>
                            <FormField<FormValues, FormPath>
                              name="amount.currency.code"
                              defaultValue={''}
                              required
                              render={({ field: { value, ref, ...field } }) => <CurrencyAutocomplete size="small" selected={value} defaultCurrencyCode={defaultCurrencyCode} {...field} />}
                            />
                          </GridCol>
                        </GridRow>
                      )}
                    </GridCol>

                    <GridCol span={1}>
                      <DT>{tGlobal('investment.durationStart')}</DT>
                    </GridCol>
                    <GridCol span={2} loading={isFetching}>
                      {!editing ? (
                        <DD isEmpty={!isDefinedString(toIntervalStartDateString(investment?.duration))}>{toIntervalStartDateString(investment?.duration)}</DD>
                      ) : (
                        <FormField<FormValues, FormPath>
                          name="duration.start"
                          render={({ field: { value, ...field } }) => (
                            <Datepicker size="small" selected={value} {...field} />
                          )}
                        />
                      )}
                    </GridCol>

                    <GridCol span={1}>
                      <DT>{tGlobal('investment.durationEnd')}</DT>
                    </GridCol>
                    <GridCol span={2} loading={isFetching}>
                      {!editing ? (
                        <DD isEmpty={!isDefinedString(toIntervalEndDateString(investment?.duration))}>{toIntervalEndDateString(investment?.duration)}</DD>
                      ) : (
                        <FormField<FormValues, FormPath>
                          name="duration.end"
                          render={({ field: { value, ...field } }) => (
                            <Datepicker size="small" selected={value} {...field} />
                          )}
                        />
                      )}
                    </GridCol>

                    <GridCol span={1}>
                      <DT>{tGlobal('investment.returnDurationStart')}</DT>
                    </GridCol>
                    <GridCol span={2} loading={isFetching}>
                      {!editing ? (
                        <DD isEmpty={!isDefinedString(toIntervalStartDateString(investment?.returnDuration))}>{toIntervalStartDateString(investment?.returnDuration)}</DD>
                      ) : (
                        <FormField<FormValues, FormPath>
                          name="returnDuration.start"
                          render={({ field: { value, ...field } }) => (
                            <Datepicker size="small" selected={value} {...field} />
                          )}
                        />
                      )}
                    </GridCol>

                    <GridCol span={1}>
                      <DT>{tGlobal('investment.returnDurationEnd')}</DT>
                    </GridCol>
                    <GridCol span={2} loading={isFetching}>
                      {!editing ? (
                        <DD isEmpty={!isDefinedString(toIntervalEndDateString(investment?.returnDuration))}>{toIntervalEndDateString(investment?.returnDuration)}</DD>
                      ) : (
                        <FormField<FormValues, FormPath>
                          name="returnDuration.end"
                          render={({ field: { value, ...field } }) => (
                            <Datepicker size="small" selected={value} {...field} />
                          )}
                        />
                      )}
                    </GridCol>
                  </GridRow>
                </div>
              </div>
            </CardContent>
          </form>
        </FormProvider>
      </Card>

      <CancelRequestModal
        open={cancelingRequest}
        onConfirm={handleConfirmCancelRequest}
        onCancel={handleCancelCancelRequest}
      />
    </>
  );
}
